import GenerateGatePass from "components/GatePass/generateGatePass";
import { GatePassTable } from "components/GatePass/passTable";
import { PassDetails } from "components/GatePass/passView";
import { useEffect, useState } from "react";
import UserApi from "apis/UserApi";
import { useNavigate } from "react-router-dom";
import { useError } from "hooks/ErrorContext";
import { useSuccess } from "hooks/SuccessContext";

export default function GatePass() {
  const navigate = useNavigate();
  const { showError } = useError();
  const { showMessage } = useSuccess();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [passes, setPasses] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const passesPerPage = 10;

  useEffect(() => {
    fetchPasses();
  }, [currentPage, statusFilter]);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      const response = await UserApi.getAllGatePasses();

      // Check for unauthorized response
      if (response?.status === 401 || response?.data?.status === 401) {
        navigate('/pages/authentication/sign-in');
        return;
      }

      const allPasses = response?.data?.data || [];

      // Apply filters
      let filteredPasses = allPasses;

      // Apply status filter
      if (statusFilter) {
        filteredPasses = filteredPasses.filter(pass =>
          pass.status.toLowerCase() === statusFilter.toLowerCase()
        );
      }

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredPasses = filteredPasses.filter(pass =>
          pass.vehicleNumber.toLowerCase().includes(query) ||
          pass.purpose.toLowerCase().includes(query)
        );
      }

      // Calculate pagination
      const startIndex = (currentPage - 1) * passesPerPage;
      const endIndex = startIndex + passesPerPage;
      const paginatedPasses = filteredPasses.slice(startIndex, endIndex);

      setPasses(paginatedPasses);
      setTotalPages(Math.ceil(filteredPasses.length / passesPerPage));
    } catch (error) {
      // Check for unauthorized error in catch block
      if (error?.response?.status === 401 || error?.response?.data?.status === 401) {
        navigate('/pages/authentication/sign-in');
        return;
      }

      showError('Failed to fetch gate passes. Please try again later.');
      console.error('Error fetching passes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsClick = (pass) => {
    setSelectedPass(pass);
    setShowDetailsModal(true);
  };

  const handleGeneratePass = async (formData) => {
    setLoading(true);
    try {
      console.log('Submitting gate pass data:', formData); // Debug log
      const response = await UserApi.createGatePass(formData);
      console.log('API Response:', response); // Debug log

      if (response?.status === 401 || response?.data?.status === 401) {
        navigate('/pages/authentication/sign-in');
        return;
      }

      // Check if the response has data
      if (response?.data) {
        showMessage('Gate pass request submitted successfully');
        setShowGenerateModal(false);
        fetchPasses(); // Refresh the list
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error generating gate pass:', error);
      console.error('Error details:', error.response?.data); // Debug log
      showError(error.response?.data?.message || error.message || 'Failed to generate gate pass. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-inter">
              Gate Pass Management
            </h1>
            <p className="mt-2 text-gray-600">
              Manage and track all your gate passes
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowGenerateModal(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center gap-2"
            >
              <i className="fas fa-plus"></i>
              Generate Pass
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 mb-8">


          <GatePassTable
            passes={passes}
            loading={loading}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onPassClick={handleDetailsClick}
          />
        </div>
      </div>

      {showGenerateModal && (
        <GenerateGatePass
          onClose={() => setShowGenerateModal(false)}
          onSubmit={handleGeneratePass}
        />
      )}

      {showDetailsModal && selectedPass && (
        <PassDetails
          pass={selectedPass}
          onClose={() => setShowDetailsModal(false)}
          onPrint={() => window.print()}
          onShare={() => {}}
        />
      )}
    </div>
  );
}

